const DB_NAME = 'line-medic-saved-iaps-v1';
const DB_VERSION = 1;
const STORE = 'iaps';

function openDb() {
  if (typeof window === 'undefined' || !window.indexedDB) {
    return Promise.reject(new Error('Offline IAP storage is not available on this device.'));
  }
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('incidentKey', 'incidentKey', { unique: false });
        store.createIndex('savedAt', 'savedAt', { unique: false });
        store.createIndex('documentUrl', 'documentUrl', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Unable to open offline IAP storage.'));
  });
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Offline IAP storage request failed.'));
  });
}

async function withStore(mode, work) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const store = transaction.objectStore(STORE);
      let result;
      try {
        result = work(store);
      } catch (error) {
        reject(error);
        return;
      }
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error || new Error('Offline IAP storage failed.'));
      transaction.onabort = () => reject(transaction.error || new Error('Offline IAP storage was interrupted.'));
    });
  } finally {
    db.close();
  }
}

export async function listSavedIaps() {
  const db = await openDb();
  try {
    const transaction = db.transaction(STORE, 'readonly');
    const rows = await requestResult(transaction.objectStore(STORE).getAll());
    return (Array.isArray(rows) ? rows : []).sort((a, b) => String(b.savedAt || '').localeCompare(String(a.savedAt || '')));
  } finally {
    db.close();
  }
}

export async function readSavedIap(id) {
  const db = await openDb();
  try {
    const transaction = db.transaction(STORE, 'readonly');
    return await requestResult(transaction.objectStore(STORE).get(String(id || '')));
  } finally {
    db.close();
  }
}

export function writeSavedIap(record) {
  if (!record?.id) return Promise.reject(new Error('Saved IAP record requires an ID.'));
  return withStore('readwrite', store => store.put(record));
}

export function deleteSavedIapRecord(id) {
  return withStore('readwrite', store => store.delete(String(id || '')));
}

export async function findSavedIapByDocumentUrl(documentUrl) {
  const value = String(documentUrl || '').trim();
  if (!value) return null;
  const db = await openDb();
  try {
    const transaction = db.transaction(STORE, 'readonly');
    const index = transaction.objectStore(STORE).index('documentUrl');
    const rows = await requestResult(index.getAll(value));
    return Array.isArray(rows) && rows.length ? rows[0] : null;
  } finally {
    db.close();
  }
}

export function iapStorageSupported() {
  return typeof window !== 'undefined' && Boolean(window.indexedDB);
}
